import { MeshMaterials } from "@/3d/material/MeshMaterials";
import { GroupInstance } from "@/3d/modelInstance/GroupInstance";
import { createSelectedItem, SELECTED_ITEM_TYPE } from "@/3d/SelectedItem";
import { FurnitureInstance } from "@/state/Furniture";
import { MaterialResources } from "@/state/MaterialResources";
import { useResourcesState } from "@/state/resourcesState";
import { THEME_COLORS } from "@/utils/colors";
import { SELECTION_OUTLINE_THICKNESS } from "@/utils/constants";
import { useSelection } from "@/utils/useSelection";
import { Outlines } from "@react-three/drei";
import { useThree } from "@react-three/fiber";
import React, { useEffect, useRef } from "react";
import * as THREE from "three";

type Props = {
    furniture: FurnitureInstance;
    disableMouseInteractions?: boolean;
    isSelected?: boolean;
};

export const FurnitureInstanceRenderer: React.FC<Props> = ({ furniture, isSelected }) => {
    const { materials } = useResourcesState();

    const instance = furniture.instance;
    const { selectedItem } = useSelection();
    const isFurnitureSelected =
        isSelected ??
        (selectedItem?.selectedItemType === SELECTED_ITEM_TYPE.FURNITURE &&
            selectedItem?.furnitureInstanceId === furniture.id);

    return (
        <group>
            {Object.values(instance.meshes)
                .filter((mesh) => !mesh.isHidden && !mesh.groupId)
                .map((mesh, index) => (
                    <mesh
                        key={index}
                        geometry={mesh.geometry}
                        castShadow
                        receiveShadow
                        userData={createSelectedItem(mesh, furniture)}
                    >
                        <Outlines
                            visible={isFurnitureSelected}
                            thickness={SELECTION_OUTLINE_THICKNESS}
                            color={THEME_COLORS.highlightedItemBlue}
                        />
                        <MeshMaterials
                            model={instance}
                            instance={mesh}
                            materials={materials}
                            isSelected={false}
                        />
                    </mesh>
                ))}
            {Object.values(instance.groups).map((group, index) => (
                <GroupInstanceRenderer
                    key={index}
                    instance={group}
                    materials={materials}
                    furniture={furniture}
                    isFurnitureSelected={isFurnitureSelected}
                />
            ))}
            {instance.elementInstances.map((elementInstance, index) => {
                return (
                    <mesh
                        key={index}
                        geometry={elementInstance.geometry}
                        position={elementInstance.area.box.center}
                        castShadow
                        receiveShadow
                        userData={createSelectedItem(elementInstance, furniture)}
                    >
                        <Outlines
                            visible={isFurnitureSelected}
                            thickness={SELECTION_OUTLINE_THICKNESS}
                            color={THEME_COLORS.highlightedItemBlue}
                        />
                        <MeshMaterials
                            model={instance}
                            instance={elementInstance}
                            materials={materials}
                            isSelected={false}
                        />
                    </mesh>
                );
            })}
        </group>
    );
};

type GroupInstanceRendererProps = {
    instance: GroupInstance;
    materials: Record<string, MaterialResources>;
    furniture: FurnitureInstance;
    isFurnitureSelected: boolean;
};

const GroupInstanceRenderer: React.FC<GroupInstanceRendererProps> = ({
    instance,
    materials,
    furniture,
    isFurnitureSelected,
}) => {
    const { invalidate } = useThree();
    const groupRef = useRef<THREE.Group>(null);

    useEffect(() => {
        const propertyChangeHandler = () => {
            if (groupRef.current) {
                groupRef.current.rotation.copy(instance.rotation);
                invalidate();
            }
        };

        if (instance.stateMachine) {
            instance.stateMachine.on("propertyChange", propertyChangeHandler);
        }

        return () => {
            if (instance.stateMachine) {
                instance.stateMachine.off("propertyChange", propertyChangeHandler);
            }
        };
    }, [instance.stateMachine, invalidate]);

    return (
        <group ref={groupRef} position={instance.position} rotation={instance.rotation}>
            {Object.values(instance.meshes).map((mesh, index) => (
                <mesh
                    key={index}
                    geometry={mesh.geometry}
                    castShadow
                    receiveShadow
                    position={instance.meshPositions[mesh.raw.name]}
                    rotation={instance.meshRotations[mesh.raw.name]}
                    userData={createSelectedItem(mesh, furniture, instance)}
                >
                    <MeshMaterials
                        model={furniture.instance}
                        instance={mesh}
                        materials={materials}
                        isSelected={false}
                    />
                    <Outlines
                        visible={isFurnitureSelected}
                        thickness={SELECTION_OUTLINE_THICKNESS}
                        color={THEME_COLORS.highlightedItemBlue}
                    />
                </mesh>
            ))}
        </group>
    );
};
